import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Send, Check, AlertCircle, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { Reveal, SectionLabel, SectionTitle } from "@/components/Reveal";
import { studio } from "@/data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAPS_EMBED = process.env.REACT_APP_GOOGLE_MAPS_EMBED;

export default function Contatti() {
  const formRef = useRef(null);
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [data, setData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    privacy: false,
  });

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setData((d) => ({ ...d, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!data.privacy) {
      setStatus({ state: "error", msg: "Devi accettare l'informativa privacy per inviare il messaggio." });
      return;
    }
    setStatus({ state: "loading", msg: "" });

    try {
      await axios.post(`${API}/contact`, {
        name: data.name,
        email: data.email,
        phone: data.phone || null,
        subject: data.subject || null,
        message: data.message,
      });
      setStatus({
        state: "success",
        msg: "Messaggio inviato. Ti risponderemo al più presto.",
      });
    } catch (err) {
      console.error("Submit failed", err);
      const detail = err?.response?.data?.detail;
      setStatus({
        state: "error",
        msg: detail || "Si è verificato un errore. Riprova più tardi o contattaci telefonicamente.",
      });
      return;
    }

    setData({ name: "", email: "", phone: "", subject: "", message: "", privacy: false });
    if (formRef.current) formRef.current.reset();
  };

  return (
    <main data-testid="contatti-page" className="bg-slate-50">
      {/* HEADER */}
      <section className="pt-36 pb-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel>Contatti</SectionLabel>
            <h1 className="font-serif font-medium mt-5 text-5xl md:text-7xl tracking-tight leading-[1.02] text-slate-900">
              Parliamone.<br />
              <em className="not-italic text-amber-800">Senza impegno.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-3xl text-lg text-slate-600 leading-relaxed">
              Compila il modulo o utilizza i contatti diretti dello studio. Ti
              risponderemo nel più breve tempo possibile.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          {/* INFO CARD */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="bg-slate-900 text-white p-8 lg:p-10">
                <div className="overline text-amber-400">Studio Cecconato</div>
                <h2 className="font-serif text-3xl mt-3">{studio.ownerTitle}</h2>
                <p className="mt-3 text-sm text-slate-300">{studio.albo}</p>

                <ul className="mt-10 space-y-6">
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full border border-amber-400 flex items-center justify-center shrink-0">
                      <MapPin size={16} className="text-amber-300" />
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-amber-400">Sede</div>
                      <div className="mt-1 text-sm">{studio.address}</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full border border-amber-400 flex items-center justify-center shrink-0">
                      <Phone size={16} className="text-amber-300" />
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-amber-400">Telefono</div>
                      <a href={studio.phoneHref} data-testid="contact-info-phone" className="mt-1 text-sm hover:text-amber-300 block">
                        {studio.phone}
                      </a>
                    </div>
                  </li>
                </ul>

                <p className="mt-8 text-sm text-slate-300 leading-relaxed">
                  Per inviarci una richiesta scritta usa il modulo qui a fianco:
                  riceveremo subito la tua email e ti risponderemo dall'indirizzo dello studio.
                </p>

                <div className="mt-10 pt-8 border-t border-slate-700">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-amber-400">Orari</div>
                  <div className="mt-3 text-sm space-y-1 text-slate-200">
                    <div className="flex justify-between">
                      <span>Lun - Ven</span>
                      <span>09:00 — 18:00</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sabato</span>
                      <span>Su appuntamento</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* MAP */}
              <div className="mt-6 aspect-[4/3] border border-slate-200 overflow-hidden bg-slate-100">
                <iframe
                  data-testid="contact-google-map"
                  src={MAPS_EMBED || "https://www.google.com/maps?q=Via+Morganella+4%2Fc%2C+31020+Villorba+TV%2C+Italia&output=embed"}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mappa studio"
                />
              </div>
            </Reveal>
          </div>

          {/* FORM */}
          <div className="lg:col-span-7">
            <Reveal>
              <form
                ref={formRef}
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="bg-white border border-slate-200 p-8 lg:p-10 space-y-6"
              >
                <div className="overline">Modulo di contatto</div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wider text-slate-700 mb-2">
                      Nome e cognome *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={data.name}
                      onChange={onChange}
                      data-testid="contact-input-name"
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-slate-900 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wider text-slate-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={data.email}
                      onChange={onChange}
                      data-testid="contact-input-email"
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-slate-900 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wider text-slate-700 mb-2">
                      Telefono
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={data.phone}
                      onChange={onChange}
                      data-testid="contact-input-phone"
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-slate-900 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-wider text-slate-700 mb-2">
                      Oggetto
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={data.subject}
                      onChange={onChange}
                      data-testid="contact-input-subject"
                      className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-slate-900 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium uppercase tracking-wider text-slate-700 mb-2">
                    Messaggio *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={6}
                    value={data.message}
                    onChange={onChange}
                    data-testid="contact-input-message"
                    className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-slate-900 transition-colors resize-none"
                  />
                </div>

                <label className="flex items-start gap-3 text-sm text-slate-600">
                  <input
                    type="checkbox"
                    name="privacy"
                    checked={data.privacy}
                    onChange={onChange}
                    data-testid="contact-input-privacy"
                    className="mt-1 accent-slate-900"
                  />
                  <span>
                    Ho letto e accetto il trattamento dei dati personali ai
                    sensi del Reg. UE 2016/679 (GDPR), ai soli fini di ricevere
                    risposta alla mia richiesta.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={status.state === "loading"}
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center gap-2 bg-slate-900 hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-4 rounded-full font-medium transition-colors"
                >
                  {status.state === "loading" ? "Invio in corso..." : "Invia messaggio"}
                  <Send size={16} />
                </button>

                {status.state === "success" && (
                  <div
                    data-testid="contact-status-success"
                    className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm"
                  >
                    <Check size={18} className="mt-0.5 shrink-0" />
                    <span>{status.msg}</span>
                  </div>
                )}
                {status.state === "error" && (
                  <div
                    data-testid="contact-status-error"
                    className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 text-red-800 text-sm"
                  >
                    <AlertCircle size={18} className="mt-0.5 shrink-0" />
                    <span>{status.msg}</span>
                  </div>
                )}
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
